import { FaChalkboardTeacher } from 'react-icons/fa';
import { KskLearningPanel } from './KskLearningPanel';
import { OffscreenDialogueView } from './OffscreenDialogueView';
import { accountUserProfileService } from '../services/accountUserProfileService';
import { OsrSourceImage } from './OsrSourceImage';
import { EDGE_VOICES, edgeSpeechPort, prefetchEdgeSpeech } from '../utils/edgeSpeechPort';
import { readEmbeddedImageRecord } from '../features/ttqib/embeddedImageUtils';
import { OsrSpeechPlayer, type SpeechPhase } from '../utils/osrSpeechPlayer';
import { OsrGenerationOptions } from './OsrGenerationOptions';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Headphones,
  MessageCircle,
  Check,
  AlertTriangle,
  ArrowLeft,
  Eye,
  FileText,
  ChevronDown,
  ListMusic,
  Loader2,
  RefreshCw,
  Pause,
  Play,
  SkipBack,
  SkipForward,
  Sparkles,
  Star,
  ThumbsDown,
  ThumbsUp,
  Trash2,
} from 'lucide-react';

import { useActiveSubject } from '../context/ActiveSubjectContext';
import { useAuth } from '../context/AuthContext';
import { useProductStudyMode } from '../context/ProductStudyModeContext';
import { resolveKnowledgeParticleQaDisplay } from './KnowledgeParticleQaSections';
import { knowledgeService } from '../services/knowledgeService';
import { getOsrSourceStats, setOsrFavorite, listPersistedOsrShards, submitOsrFeedback, enqueueOsrGeneration, clearOsrShards, listOsrGenerationJobs, retryOsrGenerationJob, deleteFailedOsrGenerationJobs, type OsrGenerationJob } from '../services/osrService';
import { getLearningSequenceSettings } from '../services/reviewService';
import type { KnowledgeParticle } from '../types/domain';
import {
  getMemoryOffscreenLibrary,
  listOffscreenPlaylistOptions,
  loadPersistedOffscreenLibrary,
  OFFSCREEN_PLAY_MODE_OPTIONS,
  persistOffscreenLibrary,
  resolveOffscreenPlaylistName,
  clearActivePlaylistContents,
  type OffscreenAdvanceMode,
  type OffscreenLibraryState,
  type OffscreenPlayMode,
  type OffscreenQualityFlag,
} from '../utils/offscreenLibraryStore';
import type { OffscreenQueueItem } from '../utils/offscreenQueueFromParticles';
import { MathTextBlock } from './MathTextBlock';
import { displayGlance, spokenQuestion, spokenAnswer } from '../utils/osrPresentation';
import { SUBJECT_CONFIG } from '../utils/subjectConfig';

type PlaybackState = 'idle' | 'loading' | 'playing' | 'paused';

const OFFSCREEN_QUEUE_RENDER_LIMIT = 24;

const EMPTY_PLAYER_ITEM: OffscreenQueueItem = {
  id: 'empty-shell',
  kind: 'shard',
  title: '暂无播放条目',
  speakText: '',
  question: '打开上方播放列表，可查看已生成的全部口播卡，或从知识颗粒素材继续生成。',
  answer: '',
  glanceText: '空队列',
  screenPolicy: 'glance',
};

function buildRefineSourceText(particles: KnowledgeParticle[]): string {
  const chunks: string[] = [];
  for (const particle of particles) {
    const id = String(particle.id || '').trim();
    if (!id) continue;
    const qa = resolveKnowledgeParticleQaDisplay({
      questionSource: particle.questions,
      answerSource: particle.answers,
      fallbackQuestionText: particle.title || particle.content,
    });
    const q = [qa.questionDisplay.main, ...qa.questionDisplay.items].filter(Boolean).join('\n');
    const a = [qa.answerDisplay.main, ...qa.answerDisplay.items].filter(Boolean).join('\n');
    const title = String(particle.title || '').trim();
    const imageUrl = extractEmbeddedImageUrl(particle.metadata);
    const block = [
      `[particle_id=${id}]`,
      imageUrl ? `[embedded_image_url=${imageUrl}]` : '',
      title,
      `question: ${q}`,
      `answer: ${a}`,
      `coachOverlay: ${JSON.stringify(particle.metadata?.coachOverlay ?? particle.metadata?.coach_overlay ?? null)}`,
      `learnerEvidence: ${JSON.stringify(particle.metadata?.learnerEvidence ?? particle.metadata?.learner_evidence ?? null)}`,
    ].filter(Boolean).join('\n');
    if (block.trim()) chunks.push(block.trim());
  }
  return chunks.join('\n\n');
}

function extractEmbeddedImageUrl(metadata: Record<string, unknown> | undefined): string | undefined {
  if (!metadata) return undefined;
  const embedded = metadata.relatedGraphEmbeddedImage || metadata.embedded_image || metadata.embeddedImage || metadata.source_image || metadata.sourceImage;
  if (typeof embedded === 'string' && embedded.trim()) return embedded.trim();
  if (embedded && typeof embedded === 'object') {
    const record = embedded as Record<string, unknown>;
    const url = record.sourceImageUrl || record.imageUrl || record.url;
    if (typeof url === 'string' && url.trim()) return url.trim();
  }
  const direct = metadata.sourceImageUrl || metadata.imageUrl;
  return typeof direct === 'string' && direct.trim() ? direct.trim() : undefined;
}

function normalizeMatchText(value: string): string {
  return String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[，。！？、；：""''（）()【】\[\]《》<>·•]/g, '');
}

/** Bind a refined shard back to a source KP (LLM id → title/question overlap → sole source). */
function resolveParticleIdForShard(
  shard: { question?: string; prompt_text?: string; source_particle_id?: string },
  particles: KnowledgeParticle[],
): string | undefined {
  const known = new Map(
    particles
      .map((p) => [String(p.id || '').trim(), p] as const)
      .filter(([id]) => Boolean(id)),
  );
  const claimed = String(shard.source_particle_id || '').trim();
  if (claimed && known.has(claimed)) return claimed;

  const hay = normalizeMatchText(`${shard.prompt_text || ''} ${shard.question || ''}`);
  if (!hay) return particles.length === 1 ? String(particles[0]?.id || '').trim() || undefined : undefined;

  let bestId = '';
  let bestScore = 0;
  for (const [id, particle] of known) {
    const title = normalizeMatchText(String(particle.title || ''));
    if (title.length >= 4 && hay.includes(title.slice(0, Math.min(title.length, 16)))) {
      const score = title.length + 20;
      if (score > bestScore) {
        bestScore = score;
        bestId = id;
      }
      continue;
    }
    const qa = resolveKnowledgeParticleQaDisplay({
      questionSource: particle.questions,
      answerSource: particle.answers,
      fallbackQuestionText: particle.title || particle.content,
    });
    const qMain = normalizeMatchText(qa.questionDisplay.main || '');
    if (qMain.length >= 6) {
      const needle = qMain.slice(0, Math.min(qMain.length, 18));
      if (hay.includes(needle) || needle.includes(hay.slice(0, 12))) {
        const score = needle.length;
        if (score > bestScore) {
          bestScore = score;
          bestId = id;
        }
      }
    }
  }
  if (bestId) return bestId;
  if (particles.length === 1) return String(particles[0]?.id || '').trim() || undefined;
  return undefined;
}

function shardsToQueueItems(
  shards: Array<{
    id?: string;
    favorited?: boolean;
    difficulty_feedback?: string;
    glance_surface?: import("../utils/osrPresentation").OsrGlance;
    question: string;
    answer: string;
    prompt_text?: string;
    screen_policy?: 'none' | 'glance';
    source_particle_id?: string;
    source_kp_id?: string;
    glance_text?: string;
    glance?: { latex?: string; spoken_question?: string; type: string; preview: string; text: string; image_request?: { enabled?: boolean; prompt?: string; model?: string }; url?: string };
  }>,
  lane: 'broadcast' | 'light_offscreen' | 'dialogue',
  sourceParticles: KnowledgeParticle[] = [],
  subject: string,
): OffscreenQueueItem[] {
  return shards.filter((shard) => !isOfflineCalculationDrill(shard.question, shard.answer)).map((shard, i) => {
    const question = String(shard.question || '').trim();
    const answer = String(shard.answer || '').trim();
    const title = String(shard.prompt_text || question).trim().slice(0, 48) || `口播卡 ${i + 1}`;
    const speakText = lane === 'broadcast'
      ? `${question} ……参考：${answer}`.slice(0, 360)
      : lane === 'dialogue'
        ? `我们围绕这道题聊：${question}`.slice(0, 280)
        : question.slice(0, 280);
    const glance = shard.glance || shard.glance_surface;
    const particleId = shard.source_kp_id || resolveParticleIdForShard(shard, sourceParticles);
    return {
      id: shard.id || `refined-shard-${i}-${question}`,
      kind: lane === 'dialogue' ? 'topic' : 'shard',
      title,
      speakText,
      shardId: shard.id,
      favorited: shard.favorited,
      difficultyFeedback: shard.difficulty_feedback,
      sourceImage: shard.glance_surface?.source_image,
      glance,
      question,
      answer,
      glanceText: displayGlance(glance, question),
      glanceImageUrl: glance?.url || (glance && "asset" in glance ? glance.asset?.url : undefined),
      screenPolicy: 'glance',
      particleId,
      sourceLabel: particleId ? 'keliagent 精炼 · 已关联颗粒' : 'keliagent 精炼',
    };
  });
}

function buildGlanceText(_subject: string, question: string, _answer: string): string {
  // A glance is a cue for retrieval; never derive it from the reference answer.
  return question.replace(/\s+/g, ' ').trim().slice(0, 42);
}

function isOfflineCalculationDrill(question: string, answer: string): boolean {
  const text = `${question} ${answer}`.replace(/\s+/g, '');
  const asksForResult = /(结果|得数|值是多少|算出|计算出|求值|等于多少|是多少[？?]?$)/u.test(question);
  const hasLongArithmetic = (text.match(/[+＋\-－×*÷/／=＝]/g) || []).length >= 2
    || (text.match(/\d+(?:\.\d+)?/g) || []).length >= 4
    || (text.match(/分之|小数|括号/g) || []).length >= 3;
  const hasThinkingFrame = /(思路|方法|起手|先看|观察|化简|简便|为什么|易错|注意|策略)/u.test(question);
  return asksForResult && hasLongArithmetic && !hasThinkingFrame;
}

function stopSpeech() {
  try {
    window.speechSynthesis?.cancel();
  } catch {
    // ignore
  }
}

/**
 * Web off-screen home player — queue from historical knowledge particles + browser TTS.
 */
export function OffscreenHomeView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSubject } = useActiveSubject();
  const { offscreenPrefs, setOffscreenInteractionMode, setOffscreenAllowGlance, setOffscreenGenerationGroups } = useProductStudyMode();
  const accent = SUBJECT_CONFIG[activeSubject]?.color || '#2f6fed';

  const [contentAgeAhead, setContentAgeAhead] = useState(1);
  const [library, setLibrary] = useState<OffscreenLibraryState>(() => getMemoryOffscreenLibrary());
  const [baseParticles, setBaseParticles] = useState<KnowledgeParticle[]>([]);
  const [kskOpen, setKskOpen] = useState(false);
  const [queue, setQueue] = useState<OffscreenQueueItem[]>([]);
  const [queueLoading, setQueueLoading] = useState(true);
  const [queueReload, setQueueReload] = useState(0);
  const [generationJobs, setGenerationJobs] = useState<OsrGenerationJob[]>([]);
  const [generationStatusError, setGenerationStatusError] = useState<string | null>(null);
  const [generationStatusLoading, setGenerationStatusLoading] = useState(true);
  const [generationReload, setGenerationReload] = useState(0);
  const [retryingGeneration, setRetryingGeneration] = useState(false);
  const producedSignatureRef = useRef('');
  const [queueError, setQueueError] = useState<string | null>(null);
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [playbackState, setPlaybackState] = useState<PlaybackState>('idle');
  const [englishVoice,setEnglishVoice]=useState(()=>{try{return localStorage.getItem('osr_edge_voice')||'';}catch{return '';}});
  const voiceRef=useRef(englishVoice);voiceRef.current=englishVoice;
  const [speechPhase, setSpeechPhase] = useState<SpeechPhase>('idle');
  const [speechPlayer] = useState(() => new OsrSpeechPlayer(edgeSpeechPort(()=>voiceRef.current), (phase, state) => { setSpeechPhase(phase); setPlaybackState(state); }, () => setRevealed(true), () => setBusyHint('语音暂时无法播放，请检查网络后重试')));
  const [busyHint, setBusyHint] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [modeSheetOpen, setModeSheetOpen] = useState(false);
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const playlistPageOpen = searchParams.get('view') === 'playlist';
  const openPlaylist = () => {
    const params = new URLSearchParams(searchParams);
    params.set('view', 'playlist');
    setSearchParams(params, { state: { playlistReturn: true } });
  };
  const closePlaylist = () => {
    setClearConfirmOpen(false);
    if (location.state?.playlistReturn) navigate(-1);
    else {
      const params = new URLSearchParams(searchParams);
      params.delete('view');
      setSearchParams(params, { replace: true });
    }
  };
  const [generationOpen, setGenerationOpen] = useState(false);
  const [generationSize, setGenerationSize] = useState<number | 'all'>(offscreenPrefs.generationGroups);
  const [estimate,setEstimate]=useState<{total:number;unarranged:number;groups:number}|null>(null);
  const [estimateError,setEstimateError]=useState('');
  const [estimateRetry,setEstimateRetry]=useState(0);
  useEffect(()=>{
    let cancelled=false;
    setEstimate(null);setEstimateError('');
    if(!generationOpen || generationSize!=='all' || !user?.id)return;
    const userId=user.id;
    void (async()=>{
      const ids=new Set<string>();
      for(let page=1;;page++){
        const res=await knowledgeService.listKnowledgeParticles({userId,subject:SUBJECT_CONFIG[activeSubject]?.label,page,pageSize:40,sort:'created_at_desc'});
        if(cancelled)return;
        const rows=Array.isArray(res?.data)?res.data:[];
        const before=ids.size;for(const row of rows)if(row.id)ids.add(String(row.id));
        if(rows.length && ids.size===before)throw new Error('素材分页异常，请重试');
        if(rows.length<40)break;
      }
      const inventory=await getOsrSourceStats(activeSubject);
      if(cancelled)return;
      const stats=new Map(inventory.map(row=>[row.source_kp_id,row]));
      let unarranged=0,groups=0;
      for(const id of ids){const row=stats.get(id);if(!row || (!row.total&&!row.pending))unarranged++;
        if(!row || (!row.suppressed&&!row.cooling&&!row.pending&&row.unheard<2))groups++;
      }
      setEstimate({total:ids.size,unarranged,groups});
    })().catch(error=>{if(!cancelled)setEstimateError(error instanceof Error?error.message:'预估读取失败');});
    return ()=>{cancelled=true;};
  },[generationOpen,generationSize,activeSubject,user?.id,estimateRetry]);

  const generationScopeRef = useRef('');
  generationScopeRef.current = `${user?.id}:${activeSubject}`;
  const generationRequestsRef = useRef(new Map<string, string>());
  const [refining, setRefining] = useState(false);
  const [refineProgress, setRefineProgress] = useState('等待模型返回…');
  const [feedbackPending, setFeedbackPending] = useState(false);
  const [qualityTarget, setQualityTarget] = useState<OffscreenQueueItem | null>(null);
  const qualityDialogRef=useRef<HTMLDialogElement>(null);
  const [qualityError,setQualityError]=useState<string|null>(null);
  const [qualityReason,setQualityReason]=useState('missing_context');
  const [qualityDetail,setQualityDetail]=useState('');
  const feedbackRequestRef = useRef<{ key: string; id: string } | null>(null);
  const [clearing, setClearing] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  const [clearError, setClearError] = useState<string | null>(null);
  const [deleteFailedSelection, setDeleteFailedSelection] = useState<{ scope: string; ids: string[] } | null>(null);
  const [deletingFailed, setDeletingFailed] = useState(false);
  const [deleteFailedError, setDeleteFailedError] = useState<string | null>(null);
  const deleteFailedDialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (deleteFailedSelection && playlistPageOpen && deleteFailedSelection.scope === `${user?.id}:${activeSubject}`) deleteFailedDialogRef.current?.showModal();
    else { deleteFailedDialogRef.current?.close(); setDeleteFailedSelection(null); }
  }, [deleteFailedSelection, playlistPageOpen, user?.id, activeSubject]);

  const clearDialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (clearConfirmOpen && playlistPageOpen) clearDialogRef.current?.showModal();
    else clearDialogRef.current?.close();
  }, [clearConfirmOpen, playlistPageOpen]);

  const playGenRef = useRef(0);
  const autoContinueRef = useRef(false);
  const expectAdvanceRef = useRef(false);
  const libraryRef = useRef(library);
  libraryRef.current = library;
  const speakCurrentRef = useRef<() => void>(() => {});

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.setTimeout(() => setToast((prev) => (prev === message ? null : prev)), 1800);
  }, []);

  const patchLibrary = useCallback((next: OffscreenLibraryState) => {
    setLibrary(next);
    void persistOffscreenLibrary(next);
  }, []);

  useEffect(() => {
    void loadPersistedOffscreenLibrary().then((saved) => {
      setLibrary(saved);

    });
  }, []);

  useEffect(()=>{if(index>=queue.length)setIndex(Math.max(0,queue.length-1));},[index,queue.length]);
  const selectedQueueIdRef = useRef<string | undefined>();
  selectedQueueIdRef.current = queue[index]?.id;
  useEffect(()=>{
    const english=activeSubject==='english';
    // Current question/answer and next card only: bounded, memory-only lookahead.
    for(const item of queue.slice(index,index+2)) {
      prefetchEdgeSpeech(english?item.question:spokenQuestion(item.question,item.glance,offscreenPrefs.allowGlance),english?'en-US':'zh-CN',englishVoice);
      prefetchEdgeSpeech(english?item.answer:spokenAnswer(item.answer),english?'en-US':'zh-CN',englishVoice);
    }
  },[queue,index,activeSubject,englishVoice,offscreenPrefs.allowGlance]);
  useEffect(() => {
    setQueue([]);
    setBaseParticles([]);
    setIndex(0);
    setRevealed(false);
    return () => {
      expectAdvanceRef.current = false;
      playGenRef.current += 1;
      speechPlayer.stop();
    };
  }, [activeSubject, user?.id, offscreenPrefs.interactionMode]);

  useEffect(() => {
    let cancelled = false;
    const userId = user?.id;
    if (!userId) {
      setQueue([]);
      setQueueLoading(false);
      return;
    }

    void (async () => {
      setQueueLoading(true);
      setQueueError(null);
      try {
        const settings = getLearningSequenceSettings(userId);
        if (!cancelled) {
          setContentAgeAhead(
            typeof settings.contentAgeAheadYears === 'number' ? settings.contentAgeAheadYears : 1,
          );
        }
        const persisted = await listPersistedOsrShards(activeSubject, { limit: OFFSCREEN_QUEUE_RENDER_LIMIT, favorites: libraryRef.current.activePlaylistId === 'favorites' });
        if (cancelled) return;
        if (persisted.length) {
          const nextQueue = shardsToQueueItems(
            persisted.slice(0, OFFSCREEN_QUEUE_RENDER_LIMIT),
            offscreenPrefs.interactionMode,
            [],
            activeSubject,
          );
          const nextIndex = nextQueue.findIndex((item) => item.id === selectedQueueIdRef.current);
          setQueue(nextQueue);
          setIndex(Math.max(nextIndex, 0));
          setQueueError(null);
        } else {
          setQueue([]);
          setQueueError(null);
        }
      } catch (e) {
        if (cancelled) return;
        setQueueError(e instanceof Error ? e.message : '装载离屏队列失败');
      } finally {
        if (!cancelled) setQueueLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeSubject, user?.id, queueReload, offscreenPrefs.interactionMode, library.activePlaylistId]);

  useEffect(() => {
    setGenerationJobs([]);
    setGenerationStatusError(null);
    setGenerationStatusLoading(true);
    producedSignatureRef.current = '';
  }, [activeSubject, user?.id]);

  useEffect(() => {
    if (!user?.id) { setGenerationStatusLoading(false); return; }
    let cancelled = false;
    let timer: number;
    const poll = async () => {
      try {
        const jobs = await listOsrGenerationJobs(activeSubject);
        if (cancelled) return;
        setGenerationJobs(jobs);
        setGenerationStatusError(null);
        const signature = jobs.filter((job) => job.status === 'succeeded').map((job) => `${job.job_id}:${job.produced_count}`).sort().join(',');
        if (signature !== producedSignatureRef.current) {
          producedSignatureRef.current = signature;
          setQueueReload((value) => value + 1);
        }
      } catch (error) {
        if (!cancelled) setGenerationStatusError(error instanceof Error ? error.message : '无法查询生成进度');
      } finally {
        if (!cancelled) {
          setGenerationStatusLoading(false);
          timer = window.setTimeout(() => { void poll(); }, 5000);
        }
      }
    };
    void poll();
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [activeSubject, user?.id, generationReload]);

  const pendingGenerationJobs = generationJobs.filter((job) => job.status === 'queued' || job.status === 'running');
  const failedGenerationJobs = generationJobs.filter((job) => job.status === 'failed');
  const generationPending = refining || pendingGenerationJobs.length > 0;
  const refreshPlaylist = () => {
    setQueueReload((value) => value + 1);
    setGenerationReload((value) => value + 1);
  };
  const retryFailedGeneration = async () => {
    if (retryingGeneration) return;
    setRetryingGeneration(true);
    const scope = generationScopeRef.current;
    try {
      for (const job of failedGenerationJobs) {
        if (generationScopeRef.current !== scope) return;
        await retryOsrGenerationJob(activeSubject, job.job_id);
        if (generationScopeRef.current !== scope) return;
        setGenerationJobs((jobs) => jobs.map((item) => item.job_id === job.job_id ? { ...item, status: 'queued', error: null } : item));
      }
      setGenerationStatusError(null);
    } catch (error) {
      if (generationScopeRef.current === scope) setGenerationStatusError(error instanceof Error ? error.message : '重试失败');
    } finally {
      setRetryingGeneration(false);
      setGenerationReload((value) => value + 1);
    }
  };

  const deleteFailedGeneration = async () => {
    if (deletingFailed || !deleteFailedSelection || deleteFailedSelection.scope !== generationScopeRef.current) return;
    const { scope, ids } = deleteFailedSelection;
    setDeletingFailed(true);
    setDeleteFailedError(null);
    try {
      const deleted = await deleteFailedOsrGenerationJobs(activeSubject, ids);
      if (generationScopeRef.current !== scope) return;
      setGenerationJobs((jobs) => jobs.filter((job) => !deleted.includes(job.job_id)));
      setDeleteFailedSelection(null);
      showToast(deleted.length === ids.length ? `已删除 ${deleted.length} 组失败任务` : `已删除 ${deleted.length} 组；其余任务状态已变化，未删除`);
      setGenerationReload((value) => value + 1);
    } catch (error) {
      if (generationScopeRef.current === scope) setDeleteFailedError(error instanceof Error ? error.message : '删除失败，请重试');
    } finally { setDeletingFailed(false); }
  };

  const hasQueue = queue.length > 0;
  const coverage = useMemo(() => {
    const counts=new Map<string,number>();for(const item of queue)if(item.particleId)counts.set(item.particleId,(counts.get(item.particleId)||0)+1);
    const covered=counts.size;const second=[...counts.values()].filter(n=>n>1).length;
    const total = Math.max(baseParticles.length,covered);
    return { total, covered, second, remaining: Math.max(total - covered, 0) };
  }, [baseParticles.length, queue]);
  const current = hasQueue
    ? (queue[Math.min(index, Math.max(queue.length - 1, 0))] || null)
    : EMPTY_PLAYER_ITEM;
  const particleId = hasQueue ? (current?.particleId || '') : '';
  const isFavorite = Boolean(current?.favorited);
  const feedbackFlags = particleId
    ? (library.feedbackByParticleId[particleId]?.flags || [])
    : [];
  const isPlaying = playbackState === 'playing';
  const isLoading = playbackState === 'loading';
  const isPaused = playbackState === 'paused';
  // A visible cover always carries its cue; pure-listening only controls speech.
  const coverGlance = displayGlance(current?.glance, current?.question || current?.glanceText || '');
  const showGlance = Boolean(coverGlance);
  const playModeTitle = OFFSCREEN_PLAY_MODE_OPTIONS.find((o) => o.key === library.playMode)?.title || '播放模式';
  const playlistName = resolveOffscreenPlaylistName(library);
  const playlistOptions = useMemo(() => listOffscreenPlaylistOptions(library), [library]);
  const laneLabel =
    offscreenPrefs.interactionMode === 'dialogue'
      ? '对话'
      : offscreenPrefs.interactionMode === 'light_offscreen'
        ? '半离屏'
        : '单播';
  const statusLine = hasQueue
    ? `${laneLabel} · ${index + 1}/${queue.length} · ${playModeTitle}`
    : `${laneLabel} · 队列为空 · ${playModeTitle}`;

  const stopSpeak = useCallback(() => {
    expectAdvanceRef.current = false;
    autoContinueRef.current = false;
    playGenRef.current += 1;
    speechPlayer.stop();
    setPlaybackState('idle');
  }, [speechPlayer]);

  const speakCurrent = useCallback(() => {
    if (!current?.speakText) {
      setBusyHint('没有可朗读的文本');
      return;
    }
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      setBusyHint('当前浏览器不支持语音朗读');
      return;
    }

    const gen = ++playGenRef.current;
    expectAdvanceRef.current = true;
    setBusyHint(null);
    setPlaybackState('loading');
    stopSpeech();

    setRevealed(false);
    const english = activeSubject === 'english';
    const ask = english ? current.question : spokenQuestion(current.question, current.glance, offscreenPrefs.allowGlance);
    speechPlayer.start({
      question: ask, answer: english ? current.answer : spokenAnswer(current.answer),
      lang: english ? 'en-US' : 'zh-CN', delay: libraryRef.current.answerDelay,
      finished: () => {
        if (gen !== playGenRef.current) return;
        const completionId = crypto.randomUUID();
        void (async () => {
          try {
            if (current.shardId) await submitOsrFeedback(current.shardId, 'listened', completionId)
              .catch(() => submitOsrFeedback(current.shardId!, 'listened', completionId));
            if (gen !== playGenRef.current) return;
            expectAdvanceRef.current = false;
            const remaining = queue.filter(item => item.id !== current.id);
            autoContinueRef.current = libraryRef.current.advanceMode === 'auto' && remaining.length > 0;
            setQueue(remaining);
            setIndex(Math.min(index, Math.max(0, remaining.length - 1)));
            setRevealed(false);
            setBusyHint(remaining.length ? '已记录收听，已安排下次复习' : '本轮已听完，稍后刷新获取新卡或到期复习');
          } catch {
            if (gen === playGenRef.current) {
              expectAdvanceRef.current = false;
              showToast('收听记录未同步，请重试后继续');
            }
          }
        })();
      },
    });
  }, [current, queue, index, activeSubject, offscreenPrefs.allowGlance, speechPlayer, showToast]);

  speakCurrentRef.current = speakCurrent;

  useEffect(() => {
    if (!autoContinueRef.current) return;
    autoContinueRef.current = false;
    speakCurrentRef.current();
  }, [index, current?.id]);

  const onTogglePlay = useCallback(() => {
    if (!hasQueue) {
      showToast('队列为空，请先在播放列表装载或生成口播卡');
      return;
    }
    if (speechPlayer.state === 'paused') {
      expectAdvanceRef.current = true;
      speechPlayer.resume();
      return;
    }
    if (speechPlayer.state === 'loading' || speechPlayer.state === 'playing') {
      speechPlayer.pause();
      return;
    }
    speakCurrent();
  }, [hasQueue, showToast, speakCurrent, speechPlayer]);

  const goPrev = useCallback(() => {
    if (!hasQueue) return;
    stopSpeak();
    setIndex((i) => Math.max(0, i - 1));
    setRevealed(false);
  }, [hasQueue, library.playMode, stopSpeak]);

  const goNext = useCallback(() => {
    if (!hasQueue) return;
    stopSpeak();
    if (library.playMode === 'random_spine' && queue.length > 1) {
      let next = index;
      while (next === index) next = Math.floor(Math.random() * queue.length);
      setIndex(next);
      setRevealed(false);
      return;
    }
    setIndex((i) => (i >= queue.length - 1 ? 0 : i + 1));
    setRevealed(false);
  }, [hasQueue, index, library.playMode, queue.length, speakCurrent, stopSpeak]);

  useEffect(() => {
    if (!('mediaSession' in navigator) || !hasQueue) return;
    const session = navigator.mediaSession;
    const handlers: Partial<Record<MediaSessionAction, () => void>> = {
      play: () => { if (speechPlayer.state !== 'playing' && speechPlayer.state !== 'loading') onTogglePlay(); },
      pause: () => speechPlayer.pause(), stop: stopSpeak, nexttrack: goNext, previoustrack: goPrev,
    };
    for (const [action, handler] of Object.entries(handlers)) {
      try { session.setActionHandler(action as MediaSessionAction, handler); } catch { /* Unsupported device action. */ }
    }
    if (typeof MediaMetadata !== 'undefined') session.metadata = new MediaMetadata({ title: current?.question || '口播卡', artist: '颗粒日记' });
    return () => { for (const action of Object.keys(handlers)) { try { session.setActionHandler(action as MediaSessionAction, null); } catch {} } session.metadata = null; };
  }, [hasQueue, current?.question, speechPlayer, onTogglePlay, stopSpeak, goNext, goPrev]);
  useEffect(() => () => { if ('mediaSession' in navigator) navigator.mediaSession.playbackState = 'none'; }, []);
  useEffect(() => {
    if ('mediaSession' in navigator) navigator.mediaSession.playbackState = isPlaying || isLoading ? 'playing' : isPaused ? 'paused' : 'none';
  }, [isPlaying, isLoading, isPaused]);

  const setPlayMode = useCallback((mode: OffscreenPlayMode) => {
    const next = { ...libraryRef.current, playMode: mode };
    patchLibrary(next);
    setModeSheetOpen(false);
    showToast(`已切换：${OFFSCREEN_PLAY_MODE_OPTIONS.find((o) => o.key === mode)?.title || mode}`);
  }, [patchLibrary, showToast]);

  const setActivePlaylist = useCallback((playlistId: string) => {
    const next = {
      ...libraryRef.current,
      activePlaylistId: playlistId,
      // Re-selecting a list restores「全部颗粒片段」after a clear.
        // Re-selecting a list restores「全部口播卡」after a clear.
      clearedBuiltinAll: false,
      playMode: playlistId === 'favorites' ? 'favorites' as OffscreenPlayMode : (
        libraryRef.current.playMode === 'favorites' ? 'newest_first' as OffscreenPlayMode : libraryRef.current.playMode
      ),
    };
    patchLibrary(next);
    showToast(`播放列表：${resolveOffscreenPlaylistName(next)}`);
  }, [patchLibrary, showToast]);

  const clearCurrentPlaylist = useCallback(async () => {
    if (clearing) return;
    const id = libraryRef.current.activePlaylistId || 'all';
    const scope = generationScopeRef.current;
    setClearing(true);
    setClearError(null);
    try {
      const ids = id === 'all' ? undefined : queue.map((item) => item.shardId).filter((value): value is string => Boolean(value));
      if (id !== 'all' && (!ids?.length || ids.length !== queue.length)) throw new Error('请刷新口播卡后再清空');
      const count = await clearOsrShards(activeSubject, ids);
      if (generationScopeRef.current !== scope) return;
      stopSpeak();
      patchLibrary(clearActivePlaylistContents(libraryRef.current));
      setQueue([]);
      setIndex(0);
      setRevealed(false);
      setQueueError(null);
      setClearConfirmOpen(false);
      showToast(`已从后台清空 ${count} 张口播卡`);
    } catch (error) {
      const message = error instanceof Error ? error.message : '清空失败，请重试';
      setClearError(message);
      showToast(message);
    } finally { setClearing(false); }
  }, [activeSubject, clearing, queue, patchLibrary, showToast, stopSpeak]);

  const generateShardsFromParticles = useCallback(async () => {
    if (refining || !user?.id) return;
    const scope = generationScopeRef.current;
    setOffscreenGenerationGroups(generationSize);
    setRefining(true);
    let submitted = 0;
    try {
      const collected: KnowledgeParticle[] = [];
      const seen = new Set<string>();
      for (let page = 1; ; page += 1) {
        setRefineProgress(`读取第 ${page} 页素材…`);
        const res = await knowledgeService.listKnowledgeParticles({
          userId: user.id, subject: SUBJECT_CONFIG[activeSubject]?.label,
          page, pageSize: 40, sort: 'created_at_desc',
        });
        if (generationScopeRef.current !== scope) throw new Error('学科或账号已切换，停止继续安排');
        const list = Array.isArray(res?.data) ? res.data : [];
        let added = 0;
        for (const particle of list) {
          if (particle.id && !seen.has(String(particle.id))) {
            seen.add(String(particle.id)); collected.push(particle); added += 1;
          }
        }
        if (!added && list.length) throw new Error('素材分页未前进，已停止，避免重复安排');

        if (list.length < 40) break;
      }
      setBaseParticles(collected);
      const inventory=await getOsrSourceStats(activeSubject);
      const stats=new Map(inventory.map(row=>[row.source_kp_id,row]));
      const eligible=collected.filter(p=>{const row=stats.get(String(p.id));return !row||(!row.suppressed&&!row.cooling&&!row.pending&&row.unheard<2);})
        .sort((a,b)=>(stats.get(String(a.id))?.total||0)-(stats.get(String(b.id))?.total||0));
      const selected = generationSize === 'all' ? eligible : eligible.slice(0, generationSize);
      if(!selected.length)throw new Error('已有待听库存或正在生成，暂不重复补货；听完后再试');
      if (!selected.length) throw new Error('当前学科没有可用素材，请先上传素材');
      let learnerContext = '';
      if (activeSubject === 'yuwen') {
        const profile = user?.id ? await accountUserProfileService.getProfileBundle(user.id).catch(()=>null) : null;
        const age = profile?.profile.derivedAgeYears;
        learnerContext = `【学习者年龄资料】${typeof age==='number' ? `按出生年份推算约 ${age} 岁（非精确周岁）` : '实际年龄未知'}；未叠加超前学习年龄。基础字词句训练不得因素材较难被省略。\n`;
      }
      if (generationScopeRef.current !== scope) throw new Error('学科或账号已切换，停止继续安排');
      const groups = selected.map((particle) => {
        const text = learnerContext + buildRefineSourceText([particle]);
        if (text.length < 12 || text.length > 24000) throw new Error('素材过短或过长，请先整理素材再生成');
        const key = `${scope}:${particle.id}:${text}`;
        let requestId = generationRequestsRef.current.get(key);
        if (!requestId) { requestId = crypto.randomUUID(); generationRequestsRef.current.set(key, requestId); }
        return { request_id: requestId, source_particle_id: String(particle.id), source_text: text, source_image: readEmbeddedImageRecord(particle.metadata as Record<string,unknown>) || undefined };
      });
      for (let offset = 0; offset < groups.length; offset += 20) {
        if (generationScopeRef.current !== scope) throw new Error('学科或账号已切换，停止继续安排');
        setRefineProgress(`正在提交 ${offset + 1}–${Math.min(offset + 20, groups.length)} / ${groups.length} 组…`);
        const result = await enqueueOsrGeneration({ subject: activeSubject, model: offscreenPrefs.osrModel, groups: groups.slice(offset, offset + 20) });
        submitted += result.job_ids.length;
        if (generationScopeRef.current !== scope) return;
        const now = new Date().toISOString();
        setGenerationJobs((jobs) => [...jobs.filter((job) => !result.job_ids.includes(job.job_id)), ...result.job_ids.map((job_id) => ({
          job_id, status: 'queued' as const, produced_count: 0, error: null, created_at: now, updated_at: now,
        }))]);
        setGenerationReload((value) => value + 1);
      }
      setBusyHint(submitted ? `已安排 ${submitted} 组后台生成，完成后自动更新列表` : '后台已有库存或生成任务，本次无需重复补货');
      setGenerationOpen(false);
      generationRequestsRef.current.clear();
      setQueueError(null);
      showToast(submitted ? `已安排 ${submitted} 组，后台自动生成` : '本次无需重复补货');
    } catch (error) {
      const message = error instanceof Error ? error.message : '安排失败';
      setBusyHint(`${message}${submitted ? `；已提交 ${submitted} 组，重试不会重复入队` : ''}`);
      setRefineProgress(message);
    } finally { setRefining(false); }
  }, [activeSubject, generationSize, offscreenPrefs.osrModel, refining, setOffscreenGenerationGroups, showToast, user?.id]);

  const openGeneration = () => {
    setGenerationSize(offscreenPrefs.generationGroups);
    setGenerationOpen(true);
  };

  const setAdvanceMode = useCallback((mode: OffscreenAdvanceMode) => {
    patchLibrary({ ...libraryRef.current, advanceMode: mode });
    showToast(mode === 'auto' ? '已开自动连播' : '已改为一键一条');
  }, [patchLibrary, showToast]);

  const onToggleLyrics = useCallback(() => {
    const next = { ...libraryRef.current, showLyrics: !libraryRef.current.showLyrics };
    patchLibrary(next);
    showToast(next.showLyrics ? '已显示口播文案' : '已显示封面位');
  }, [patchLibrary, showToast]);

  const onToggleFavorite = useCallback(async () => {
    if (!current.shardId || feedbackPending) return;
    const id=current.shardId, value=!current.favorited, scope=generationScopeRef.current;
    setFeedbackPending(true);
    try{await setOsrFavorite(id,value);if(scope!==generationScopeRef.current)return;
      setQueue(items=>items.map(item=>item.shardId===id?{...item,favorited:value}:item).filter(item=>libraryRef.current.activePlaylistId!=='favorites'||item.favorited));
      showToast(value?'已加入收藏列表':'已移出收藏');
    }catch(error){showToast(error instanceof Error?error.message:'收藏失败');}finally{setFeedbackPending(false);}
  },[current,feedbackPending,showToast]);

  useEffect(()=>{if(qualityTarget)qualityDialogRef.current?.showModal();else qualityDialogRef.current?.close();},[qualityTarget]);
  useEffect(()=>{setQualityTarget(null);feedbackRequestRef.current=null;},[activeSubject,user?.id]);
  const skipDiscarded = (item:OffscreenQueueItem) => {
    const continuePlaying=speechPlayer.state==='playing'||speechPlayer.state==='loading';
    stopSpeak();setRevealed(false);
    setQueue(items=>items.filter(card=>card.id!==item.id));
    setIndex(i=>Math.min(i,Math.max(0,queue.length-2)));
    if(continuePlaying)autoContinueRef.current=true;
  };
  const onToggleFlag = async (flag:OffscreenQualityFlag, item=current, reason?:string, detail?:string) => {
    if(feedbackPending || !item.shardId)return;
    const scope=generationScopeRef.current;
    const key=`${item.shardId}:${flag}:${reason||''}:${detail||''}`;
    if(feedbackRequestRef.current?.key!==key)feedbackRequestRef.current={key,id:crypto.randomUUID()};
    setFeedbackPending(true);setQualityError(null);
    const skippedEarly=reason==='screenless' && current.id===item.id;
    if(skippedEarly)skipDiscarded(item);
    try{
      await submitOsrFeedback(item.shardId,flag,feedbackRequestRef.current.id,reason,detail);
      if(scope!==generationScopeRef.current)return;
      feedbackRequestRef.current=null;setQualityTarget(null);
      if(flag==='just_right')setQueue(items=>items.map(card=>card.id===item.id?{...card,difficultyFeedback:'just_right'}:card));
      if(flag!=='just_right'){
        if(!skippedEarly && current.id===item.id)skipDiscarded(item);
        else setQueue(items=>items.filter(card=>card.id!==item.id));
      }
      setGenerationReload(v=>v+1);
      showToast(flag==='just_right'?'已记录：难度合适，后续继续安排':flag==='too_easy'?'已回收并跳过，减少同来源推送':flag==='too_hard'?'先练更简单的卡，原卡明天再复习':'已回收并跳过，原因已交后台处理');
    }catch(error){const message=error instanceof Error?error.message:'反馈未保存，请重试';setQualityError(message);showToast(message);if(skippedEarly)setQueue(items=>items.some(card=>card.id===item.id)?items:[...items,item]);}
    finally{setFeedbackPending(false);}
  };
  const reportQuality=()=>{
    if(!offscreenPrefs.allowGlance){void onToggleFlag('poor_quality',current,'screenless');return;}
    setQualityError(null);setQualityReason('missing_context');setQualityDetail('');setQualityTarget(current);
  };

  const waveformBars = useMemo(
    () => Array.from({ length: 28 }, (_, i) => 6 + ((i * 7) % 18)),
    [],
  );

  const progress = queue.length <= 1 ? 1 : index / Math.max(queue.length - 1, 1);

  if (offscreenPrefs.interactionMode !== 'dialogue' && queueLoading && !playlistPageOpen && !queue.length) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-4">
        <Loader2 className="animate-spin text-primary" size={28} />
        <p className="text-sm text-muted">正在加载口播卡…</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      {kskOpen && <KskLearningPanel onClose={()=>setKskOpen(false)}/>}
      {toast ? (
        <div role="status" className="fixed left-1/2 top-16 z-[100] -translate-x-1/2 rounded-full bg-ink/90 px-3 py-1.5 text-xs text-white shadow-lg">
          {toast}
        </div>
      ) : null}

      <div className={playlistPageOpen ? 'hidden' : 'flex min-h-0 flex-1 flex-col'}>
      <div className="flex shrink-0 items-center justify-between gap-2 px-1 pt-2 sm:px-2 sm:pt-3">
        <details className="relative" onKeyDown={event=>{if(event.key==='Escape')event.currentTarget.open=false;}} onBlur={event=>{if(!event.currentTarget.contains(event.relatedTarget as Node))event.currentTarget.open=false;}}>
          <summary aria-label="离屏模式" className="flex cursor-pointer list-none items-center gap-2 rounded-full border border-border bg-surface px-4 py-2 text-xs font-semibold shadow-sm [&::-webkit-details-marker]:hidden"><Headphones size={16} className="text-primary" />{offscreenPrefs.interactionMode === 'dialogue' ? '离屏对话' : '单播 · 纯听'}<ChevronDown size={14} className="text-muted" /></summary>
          <div className="absolute left-0 top-full z-30 mt-2 w-64 rounded-2xl border border-border bg-surface p-2 shadow-lg">
            <button type="button" onClick={event=>{stopSpeak();setOffscreenInteractionMode('broadcast');event.currentTarget.closest('details')?.removeAttribute('open');}} className={`flex w-full items-center gap-3 rounded-xl p-3 text-left ${offscreenPrefs.interactionMode === 'dialogue' ? 'hover:bg-canvas' : 'bg-primary/10 text-primary'}`}><Headphones size={20} /><span className="flex-1"><span className="block text-sm font-semibold">单播 · 纯听</span><span className="text-xs text-muted">听题、思考、听答案</span></span>{offscreenPrefs.interactionMode !== 'dialogue' ? <Check size={16} /> : null}</button>
            <button type="button" onClick={event=>{stopSpeak();setOffscreenInteractionMode('dialogue');event.currentTarget.closest('details')?.removeAttribute('open');}} className={`mt-1 flex w-full items-center gap-3 rounded-xl p-3 text-left ${offscreenPrefs.interactionMode === 'dialogue' ? 'bg-primary/10 text-primary' : 'hover:bg-canvas'}`}><MessageCircle size={20} className="text-primary" /><span><span className="block text-sm font-semibold">离屏对话</span><span className="text-xs text-muted">围绕知识颗粒，一问一答复习</span></span></button>
          </div>
        </details>
        <button type="button" aria-label="打开知识补充课程" title="KSK 补充课程" onClick={()=>{stopSpeak();setKskOpen(true);}} className="flex min-h-11 min-w-11 items-center justify-center rounded-full text-primary"><FaChalkboardTeacher size={20}/></button>
        {offscreenPrefs.interactionMode !== 'dialogue' && (
        <button
          type="button"
          onClick={() => {
            setModeSheetOpen(false);
            openPlaylist();
          }}
          className="inline-flex max-w-[48%] shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold"
          style={{ borderColor: `${accent}55`, color: accent }}
          aria-label="播放列表"
          title={playlistName}
        >
          <ListMusic size={14} strokeWidth={2.2} />
          <span className="truncate">{playlistName}</span>
        </button>
        )}
      </div>

      {offscreenPrefs.interactionMode === 'dialogue' ? <OffscreenDialogueView key={`${user?.id}:${activeSubject}`} scope={`${user?.id}:${activeSubject}`} subject={activeSubject}/> : (
      <div className="mx-auto flex min-h-0 w-full max-w-xl flex-1 flex-col gap-2 px-3 pb-2 pt-2 sm:gap-3 sm:px-4 sm:pb-3 sm:pt-3">
      <div className="shrink-0 space-y-2 text-center">
        <p className="truncate text-[11px] text-muted">
          {statusLine}
          {' · '}
          {library.advanceMode === 'auto' ? '自动连播' : '一条一条'}
          {' · 超前+'}
          {contentAgeAhead}
        </p>
        <h1 className="line-clamp-2 text-base font-semibold text-ink sm:text-xl">{current.title}</h1>

        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => {
              setModeSheetOpen((v) => !v);
            }}
            className="rounded-full border px-3 py-1 text-xs font-semibold"
            style={{ borderColor: `${accent}55`, color: accent }}
          >
            播放模式
          </button>
          <button
            type="button"
            onClick={() => setAdvanceMode(library.advanceMode === 'auto' ? 'manual' : 'auto')}
            className="rounded-full border px-3 py-1 text-xs font-semibold"
            style={{ borderColor: `${accent}55`, color: accent }}
          >
            {library.advanceMode === 'auto' ? '自动连播' : '一条一条'}
          </button>
          <button
            type="button"
            onClick={onToggleLyrics}
            className="rounded-full border px-3 py-1 text-xs font-semibold"
            style={{ borderColor: `${accent}55`, color: accent }}
          >
            {library.showLyrics ? '封面' : '口播文案'}
          </button>
          <button
            type="button"
            onClick={() => {
              const linkedId = particleId
                || (current && current.id !== 'empty-shell'
                  ? resolveParticleIdForShard(
                    { question: current.question, prompt_text: current.title },
                    baseParticles,
                  )
                  : undefined);
              if (!linkedId) {
                showToast('当前口播卡未关联知识颗粒，请重新「生成口播卡」或切回全部颗粒片段');
                                showToast('当前口播卡未关联知识颗粒，请重新「生成口播卡」或切回全部口播卡');
                return;
              }
              stopSpeak();
              navigate(`/knowledge/${linkedId}`);
            }}
            className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold"
            style={{ borderColor: `${accent}55`, color: accent }}
          >
            <FileText size={13} strokeWidth={2.2} />
            详情
          </button>
        </div>

        {modeSheetOpen ? (
          <div className="space-y-1 rounded-xl border border-border bg-surface p-2 text-left">
            {OFFSCREEN_PLAY_MODE_OPTIONS.map((opt) => {
              const active = opt.key === library.playMode;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => setPlayMode(opt.key)}
                  className={`w-full rounded-lg px-3 py-2 text-left ${
                    active ? 'bg-[#EAF2FF]' : 'hover:bg-canvas'
                  }`}
                >
                  <div className={`text-sm font-semibold ${active ? 'text-primary-strong' : 'text-ink'}`}>
                    {opt.title}
                  </div>
                  <div className="text-[11px] text-muted">{opt.subtitle}</div>
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      <div
        className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-surface"
        style={{ borderColor: `${accent}44` }}
      >
        {library.showLyrics ? (
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4 text-center">
            {showGlance ? (
              <div className="space-y-2">
                <OsrSourceImage key={current.id} card={current} />
                <MathTextBlock text={coverGlance} className="text-xl" />
              </div>
            ) : null}
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink">{activeSubject === 'english' ? current.question : spokenQuestion(current.question, current.glance, offscreenPrefs.allowGlance)}</p>
            {revealed ? (
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted">{current.answer}</p>
            ) : (
              <button
                type="button"
                onClick={() => setRevealed(true)}
                className="inline-flex items-center gap-1.5 rounded-full bg-canvas px-3 py-1.5 text-sm font-semibold"
                style={{ color: accent }}
              >
                <Eye size={16} strokeWidth={2.2} />
                看参考答
              </button>
            )}
            {busyHint ? <p className="text-xs text-muted">{busyHint}</p> : null}
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-6 py-4 text-center sm:gap-3 sm:py-6">
            {showGlance ? (
              <div className="w-full space-y-4 overflow-auto py-4">
                <OsrSourceImage key={current.id} card={current} />
                <MathTextBlock text={coverGlance || current.title} className="text-xl sm:text-2xl" />
                {current.glance?.latex && current.glance?.text ? <p className="text-sm text-muted">{current.glance.text}</p> : null}
              </div>
            ) : <p className="text-sm text-muted">纯听模式</p>}
            <p className="text-xs text-muted">先看题面，再听短问；点「口播文案」查看提问与参考答</p>
            {busyHint ? <p className="text-xs text-muted">{busyHint}</p> : null}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center justify-around px-2">
        <button type="button" disabled={feedbackPending || !current.shardId} onClick={onToggleFavorite} className="flex flex-col items-center gap-1 text-[10px] text-muted">
          <Star size={20} color={isFavorite ? accent : '#8B8B93'} fill={isFavorite ? accent : 'transparent'} strokeWidth={2.2} />
          收藏
        </button>
        <button type="button" disabled={feedbackPending || !current.shardId} onClick={() => { void onToggleFlag('too_hard'); }} className="flex flex-col items-center gap-1 text-[10px] text-muted">
          <ThumbsDown size={20} color={feedbackFlags.includes('too_hard') ? accent : '#8B8B93'} strokeWidth={2.2} />
          太难
        </button>
        <button type="button" disabled={feedbackPending || !current.shardId} aria-pressed={current.difficultyFeedback==='just_right'} onClick={()=>{void onToggleFlag('just_right');}} className="flex flex-col items-center gap-1 text-[10px] text-muted"><ThumbsUp size={20} color={current.difficultyFeedback==='just_right'?accent:'#8B8B93'} strokeWidth={2.2} /><span>还行</span></button>
        <button type="button" disabled={feedbackPending || !current.shardId} onClick={() => { void onToggleFlag('too_easy'); }} className="flex flex-col items-center gap-1 text-[10px] text-muted">
          <ThumbsUp size={20} color={feedbackFlags.includes('too_easy') ? accent : '#8B8B93'} strokeWidth={2.2} />
          太简单
        </button>
        <button type="button" disabled={feedbackPending || !current.shardId} onClick={reportQuality} className="flex flex-col items-center gap-1 text-[10px] text-muted">
          <AlertTriangle size={20} color={feedbackFlags.includes('poor_quality') ? '#C50F1F' : '#8B8B93'} strokeWidth={2.2} />
          质量差
        </button>
      </div>

      <div className="shrink-0 space-y-2 rounded-2xl border border-border bg-surface px-4 py-3 sm:space-y-3 sm:py-4">
        <div className="flex h-6 items-end justify-center gap-[3px] sm:h-8" aria-hidden>
          {waveformBars.map((h, i) => (
            <span
              key={`bar-${i}`}
              className="w-[3px] rounded-full"
              style={{
                height: Math.max(6, Math.round(h * 0.75)),
                backgroundColor: isPlaying ? accent : '#D0D0D6',
                opacity: isPlaying ? 0.55 + (i % 5) * 0.08 : 0.35,
              }}
            />
          ))}
        </div>

        {activeSubject==='english'?<label className="flex items-center justify-center gap-2 text-xs">英语音色<select aria-label="英语音色" className="max-w-[220px] rounded-lg border border-border bg-surface p-1" value={englishVoice} onChange={e=>{stopSpeak();setEnglishVoice(e.target.value);try{localStorage.setItem('osr_edge_voice',e.target.value);}catch{}}}><option value="">Edge · Aria（默认）</option>{EDGE_VOICES.map(v=><option key={v} value={v}>{v.replace('Neural','')}</option>)}</select></label>:null}
        <div className="flex flex-wrap items-center justify-center gap-2 text-xs">
          <label>答案间隔 <select aria-label="答案间隔模式" value={library.answerDelay === 'manual' ? 'manual' : 'timed'} onChange={(event) => { stopSpeak(); patchLibrary({ ...libraryRef.current, answerDelay: event.target.value === 'manual' ? 'manual' : 5 }); }} className="rounded-lg border border-border bg-surface px-2 py-1"><option value="timed">定时</option><option value="manual">手动</option></select></label>
          {library.answerDelay !== 'manual' ? <label><input aria-label="答案等待秒数" type="number" min={0} max={300} step={1} value={library.answerDelay} onChange={(event) => { stopSpeak(); patchLibrary({ ...libraryRef.current, answerDelay: Math.max(0, Math.min(300, Math.round(Number(event.target.value) || 0))) }); }} className="w-16 rounded-lg border border-border bg-surface px-2 py-1" /> 秒</label> : null}
          {speechPhase === 'waiting' ? <button type="button" onClick={() => speechPlayer.answerNow()} className="rounded-full border border-border px-3 py-1 text-primary-strong">播放答案</button> : null}
        </div>
        <p role="status" className="text-center text-xs text-muted">{speechPhase === 'waiting' ? (isPaused ? '等待继续 · 点击播放答案' : '思考时间 · 可暂停或立即播放答案') : speechPhase === 'question' ? '正在朗读问题' : speechPhase === 'answer' ? '正在朗读答案' : ' '}</p>
        <div className="flex items-center justify-center gap-8 sm:gap-10">
          <button
            type="button"
            onClick={goPrev}
            disabled={!hasQueue || index <= 0 || isLoading}
            className="disabled:opacity-40"
            aria-label="上一首"
          >
            <SkipBack size={26} strokeWidth={2.2} />
          </button>
          <button
            type="button"
            onClick={onTogglePlay}
            disabled={!hasQueue}
            className="flex h-14 w-14 items-center justify-center rounded-full text-white shadow-md disabled:opacity-40 sm:h-[72px] sm:w-[72px]"
            style={{ backgroundColor: accent }}
            aria-label={isPlaying || isLoading ? '暂停' : '播放'}
          >
            {isLoading
              ? <Loader2 className="animate-spin" size={26} />
              : (isPlaying
                ? <Pause size={28} strokeWidth={2.4} />
                : <Play size={28} strokeWidth={2.4} className="ml-0.5" />)}
          </button>
          <button type="button" onClick={goNext} disabled={!hasQueue || isLoading} className="disabled:opacity-40" aria-label="下一首">
            <SkipForward size={26} strokeWidth={2.2} />
          </button>
        </div>

        <div className="relative mx-auto h-3.5 w-[88%]">
          <div className="absolute inset-x-0 top-1.5 h-1 rounded-full bg-border" />
          <div
            className="absolute left-0 top-1.5 h-1 rounded-full"
            style={{ width: `${Math.round(progress * 100)}%`, backgroundColor: accent }}
          />
          <div
            className="absolute top-0 h-3.5 w-3.5 -translate-x-1/2 rounded-full border-2 bg-white"
            style={{ left: `${Math.round(progress * 100)}%`, borderColor: accent }}
          />
        </div>
      </div>

      </div>
      )}
      </div>

      {generationOpen ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30 p-4" role="dialog" aria-modal="true" aria-label="生成口播卡">
          <div className="w-full max-w-lg space-y-4 rounded-2xl bg-surface p-5">
            <h2 className="text-lg font-semibold">生成多少组口播卡？</h2>
            <OsrGenerationOptions value={generationSize} onChange={setGenerationSize} />
            {generationSize==='all' && <div role="status" className="rounded-xl border border-border bg-canvas p-4 text-sm">
              {estimate ? <div className="space-y-2">
                <p>共 {estimate.total} 个知识颗粒 · 尚无成卡且未在排队 {estimate.unarranged} 个</p>
                <p className="font-semibold">本次预计安排 {estimate.groups} 组</p>
                <p>{estimate.groups ? `粗估生成 ${Math.max(1,Math.ceil(estimate.groups/2*0.5))}–${Math.max(1,Math.ceil(estimate.groups/2*2))} 分钟` : '暂无需要生成或补货的素材'}</p>
                <p className="text-xs text-muted">已排除待听库存充足、正在生成、反馈抑制及冷却中的素材；包含可补货素材。耗时暂按每组 30–120 秒、2 路并行估算，不含其他排队和重试。提交时会重新核对。</p>
              </div> : estimateError ? <div><p>{estimateError}</p><button type="button" className="mt-2 rounded-full border border-border px-3 py-1 text-primary" onClick={()=>setEstimateRetry(v=>v+1)}>重新预估</button></div> : <p className="animate-pulse">正在核对全部素材和后台库存…</p>}
            </div>}
            <p className="text-xs text-muted">本次选择会保存为学习设置中的默认生成体量。</p>
            {busyHint ? <p role="status" className="text-sm text-muted">{busyHint}</p> : null}
            <div className="flex justify-end gap-3">
              <button type="button" disabled={refining} onClick={() => setGenerationOpen(false)}>取消</button>
              <button type="button" disabled={refining || (generationSize==='all' && (!estimate || !estimate.groups))} onClick={() => { void generateShardsFromParticles(); }} className="rounded-full bg-primary px-4 py-2 text-white">
                {refining ? refineProgress : '安排生成'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {playlistPageOpen ? (
        <section aria-label="口播卡列表" className="flex min-h-0 flex-1 flex-col bg-surface">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="flex shrink-0 flex-col gap-2 border-b border-border px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <h1 className="text-base font-semibold text-ink">口播卡列表</h1>
                <button
                  type="button"
                  className="rounded-full px-3 py-1 text-xs font-semibold text-muted hover:bg-canvas"
                  onClick={closePlaylist}
                >
                  返回
                </button>
              </div>

              <div className="flex items-center justify-center">
                <label className="relative inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold"
                  style={{ borderColor: `${accent}55`, color: accent }}
                >
                  <ListMusic size={15} strokeWidth={2.2} />
                  <select
                    className="max-w-[14rem] appearance-none bg-transparent pr-5 text-sm font-semibold outline-none"
                    style={{ color: accent }}
                    value={library.activePlaylistId || 'all'}
                    aria-label="切换播放列表"
                    onChange={(e) => setActivePlaylist(e.target.value)}
                  >
                    {playlistOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.name}{opt.builtin ? ' · 系统' : ''}
                      </option>
                    ))}
                  </select>
                  <ChevronDown size={14} strokeWidth={2.2} className="pointer-events-none absolute right-2.5" />
                </label>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-[11px] text-muted">
                              当前：{playlistName} · {queue.length} 张口播卡{baseParticles.length > 0 ? ` · 素材覆盖 ${coverage.covered}/${coverage.total}` : ''}
                            </p>
                <div className="flex shrink-0 items-center gap-1.5">
                  <button type="button" onClick={refreshPlaylist} disabled={queueLoading} className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold disabled:opacity-40" style={{ borderColor: `${accent}66`, color: accent }}>
                    <RefreshCw size={12} className={queueLoading ? 'animate-spin' : ''} />{queueLoading ? '正在刷新…' : '刷新口播卡'}
                  </button>
                  <button
                    type="button"
                    onClick={openGeneration}
                    disabled={refining || !user?.id}
                    className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold disabled:opacity-40"
                    style={{ borderColor: `${accent}66`, color: accent }}
                    title="从当前学科素材提取生成口播卡"
                  >
                    {refining
                      ? <Loader2 size={12} className="animate-spin" strokeWidth={2.2} />
                      : <Sparkles size={12} strokeWidth={2.2} />}
                    生成口播卡
                  </button>
                  <button
                    type="button"
                    onClick={() => { setClearError(null); setClearConfirmOpen(true); }}
                    disabled={clearing || queue.length === 0}
                    className="inline-flex items-center gap-1 rounded-full border border-border px-2.5 py-1 text-[11px] font-semibold text-ink disabled:opacity-40"
                    title={queue.length === 0 ? '列表已是空的' : '清空前需要确认'}
                  >
                    <Trash2 size={12} strokeWidth={2.2} />
                    {clearing ? '正在清空…' : '清空列表'}
                  </button>

                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-2">
              {generationPending ? (
                <div role="status" className="mb-3 flex items-center gap-2 rounded-xl border border-border bg-canvas px-4 py-3 text-sm text-muted">
                  <Loader2 size={18} className="shrink-0 animate-spin" />
                  <div><p className="font-semibold text-ink">{refining ? refineProgress : `后台生成中 · ${pendingGenerationJobs.length} 组待完成`}</p>
                    <p className="mt-1 text-xs">{pendingGenerationJobs.some((job) => job.status === 'running') ? '正在生成口播卡' : '任务已排队，等待后台处理'}，完成后会自动显示，离开页面不影响生成。</p></div>
                </div>
              ) : null}
              {failedGenerationJobs.length > 0 ? (
                <div role="alert" className="mb-3 space-y-2 rounded-xl border border-border bg-canvas px-4 py-3 text-sm">
                  <p className="font-semibold text-danger">{failedGenerationJobs.length} 组生成失败</p>
                  {[...new Set(failedGenerationJobs.map((job) => job.error || '未能生成口播卡'))].map((error) => <p key={error} className="text-muted">{error}</p>)}
                  <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => { void retryFailedGeneration(); }} disabled={retryingGeneration || deletingFailed} className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold disabled:opacity-40" style={{ borderColor: `${accent}66`, color: accent }}>
                    <RefreshCw size={13} className={retryingGeneration ? 'animate-spin' : ''} />{retryingGeneration ? '正在重试…' : '重试失败任务'}
                  </button>
                  <button type="button" disabled={retryingGeneration || deletingFailed} onClick={() => { setDeleteFailedError(null); setDeleteFailedSelection({ scope: generationScopeRef.current, ids: failedGenerationJobs.map((job) => job.job_id) }); }} className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-danger disabled:opacity-40">
                    <Trash2 size={13} />删除失败任务
                  </button>
                  </div>
                </div>
              ) : null}
              {generationStatusError ? <p role="alert" className="mb-3 rounded-xl border border-border px-4 py-3 text-sm text-danger">{generationStatusError}</p> : null}
              {queue.length === 0 && (queueLoading || generationPending || generationStatusLoading) ? (
                <div aria-busy="true" aria-label="等待口播卡" className="space-y-3 p-3">
                  <p role="status" className="text-center text-sm text-muted">{generationPending ? '口播卡生成后将在这里显示' : '正在加载口播卡与生成进度…'}</p>
                  {[0, 1, 2, 3].map((row) => <div key={row} aria-hidden="true" className="flex animate-pulse items-center gap-3 rounded-xl border border-border p-4 motion-reduce:animate-none"><div className="h-6 w-6 rounded bg-border/60" /><div className="h-4 w-2/3 rounded bg-border/60" /></div>)}
                </div>
              ) : queue.length === 0 ? (
                <div className="flex flex-col items-center gap-3 px-3 py-6 text-center">
                  <p className="text-sm text-muted">
                    {queueError || (failedGenerationJobs.length ? '暂无可播放的口播卡，请重试失败任务或更换素材。' : generationStatusError ? '暂时无法确认后台生成状态' : '当前列表没有可播放条目')}
                  </p>
                  {queueError && !queueLoading ? (
                    <button type="button" onClick={refreshPlaylist} className="inline-flex items-center gap-1 rounded-full border px-4 py-2 text-sm font-semibold" style={{ borderColor: `${accent}66`, color: accent }}>
                      <RefreshCw size={16} />重新加载口播卡
                    </button>
                  ) : null}
                  <button
                    type="button"
                    onClick={openGeneration}
                    disabled={refining || !user?.id}
                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
                    style={{ backgroundColor: accent }}
                  >
                    {refining
                      ? <Loader2 size={16} className="animate-spin" />
                      : <Sparkles size={16} strokeWidth={2.2} />}
                    {refining ? refineProgress : '提取生成口播卡'}
                  </button>
                  <div className="flex flex-wrap items-center justify-center gap-3 text-xs">
                    <Link to="/assets" className="text-primary-strong hover:underline">去上传素材</Link>
                    <Link to="/settings/learning" className="text-muted hover:underline">学习设置</Link>
                  </div>
                </div>
              ) : (
                queue.map((item, i) => {
                  const active = i === index;
                  return (
                    <button
                      key={`${item.particleId || item.title}-${i}`}
                      type="button"
                      onClick={() => {
                        stopSpeak();
                        setIndex(i);
                        setRevealed(false);
                        setPlaybackState('idle');
                        closePlaylist();
                      }}
                      className={`mb-1 flex w-full gap-3 rounded-lg px-3 py-2.5 text-left ${
                        active ? 'bg-[#EAF2FF]' : 'hover:bg-canvas'
                      }`}
                    >
                      <span
                        className={`w-6 shrink-0 text-center text-xs font-semibold ${
                          active ? 'text-primary-strong' : 'text-muted'
                        }`}
                      >
                        {i + 1}
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col gap-1">
                        <span className={`truncate text-sm ${active ? 'font-semibold text-ink' : 'text-ink'}`}>
                          {item.title}
                        </span>
                        <span className="self-end font-mono text-[10px] font-normal text-muted" title={item.particleId ? `来源知识颗粒：${item.particleId}` : '该口播卡尚未关联来源知识颗粒'}>
                          {item.particleId ? `颗粒 · ${item.particleId.length > 12 ? `${item.particleId.slice(0, 8)}…${item.particleId.slice(-4)}` : item.particleId}` : '未关联颗粒'}
                        </span>
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </section>
      ) : null}
      <dialog ref={qualityDialogRef} aria-labelledby="quality-title" onCancel={()=>setQualityTarget(null)} onClose={()=>setQualityTarget(null)} className="m-auto w-[calc(100%-2rem)] max-w-md max-h-[90dvh] overflow-y-auto rounded-2xl border border-border bg-surface p-5 text-ink backdrop:bg-black/40">
        <h2 id="quality-title" className="text-lg font-semibold">这张卡哪里不好？</h2>
        <p className="mt-2 text-sm text-muted">提交后回收并跳过，后台根据原因处理。</p>
        <fieldset disabled={feedbackPending} className="my-4 grid grid-cols-2 gap-2">
          <legend className="sr-only">质量问题原因</legend>
          {Object.entries({wrong_answer:'答案错误',missing_context:'题意不清或缺条件',missing_image:'缺少必要图片',answer_leaked:'题面泄露答案',unclear_audio:'朗读不清或音色差',bland_content:'内容寡淡',strange_content:'奇怪',forced_question:'出题牵强',other:'其他问题'}).map(([value,label])=>(
            <label key={value} className={`flex cursor-pointer items-center gap-2 rounded-xl border p-3 text-sm ${qualityReason===value?'border-primary bg-primary/10':'border-border'}`}>
              <input type="radio" name="quality-reason" value={value} checked={qualityReason===value} onChange={()=>setQualityReason(value)} className="accent-primary" />{label}
            </label>
          ))}
        </fieldset>
        {qualityReason==='other' && <label className="mb-4 block text-sm">请说明具体问题
          <textarea value={qualityDetail} onChange={event=>setQualityDetail(event.target.value)} disabled={feedbackPending} maxLength={500} rows={3} required placeholder="哪里不好？请写下你的反馈…" className="mt-2 w-full rounded-xl border border-border bg-surface p-3" />
          <span className="text-xs text-muted">{qualityDetail.length}/500</span>
        </label>}
        {qualityError?<p role="alert" className="mb-3 text-sm text-danger">{qualityError}</p>:null}
        <div className="flex justify-end gap-3"><button disabled={feedbackPending} onClick={()=>setQualityTarget(null)}>取消</button><button disabled={feedbackPending || (qualityReason==='other' && !qualityDetail.trim())} onClick={()=>{if(qualityTarget)void onToggleFlag('poor_quality',qualityTarget,qualityReason,qualityReason==='other'?qualityDetail.trim():undefined);}} className="rounded-full bg-primary px-4 py-2 text-white">{feedbackPending?'正在提交…':'回收并跳过'}</button></div>
      </dialog>
      <dialog ref={deleteFailedDialogRef} aria-labelledby="delete-failed-title"
        onCancel={(event) => { if (deletingFailed) event.preventDefault(); else setDeleteFailedSelection(null); }}
        onClose={() => setDeleteFailedSelection(null)}
        className="m-auto w-[calc(100%-2rem)] max-w-md rounded-2xl border border-border bg-surface p-5 text-ink shadow-xl backdrop:bg-black/40">
        <h2 id="delete-failed-title" className="text-lg font-semibold">删除失败任务？</h2>
        <p className="mt-3 text-sm text-muted">将从列表移除这 {deleteFailedSelection?.ids.length || 0} 组失败任务，原始素材和已生成的口播卡会保留。正在处理的任务不会删除。</p>
        {deleteFailedError ? <p role="alert" className="mt-3 text-sm text-danger">{deleteFailedError}</p> : null}
        <div className="mt-5 flex justify-end gap-3">
          <button type="button" autoFocus disabled={deletingFailed} onClick={() => setDeleteFailedSelection(null)} className="rounded-full border border-border px-4 py-2 text-sm disabled:opacity-40">取消</button>
          <button type="button" disabled={deletingFailed} onClick={() => { void deleteFailedGeneration(); }} className="rounded-full bg-danger px-4 py-2 text-sm text-white disabled:opacity-40">{deletingFailed ? '正在删除…' : '确认删除'}</button>
        </div>
      </dialog>
      <dialog ref={clearDialogRef} aria-labelledby="clear-playlist-title"
        onCancel={(event) => { if (clearing) event.preventDefault(); else setClearConfirmOpen(false); }}
        onClose={() => setClearConfirmOpen(false)}
        className="m-auto w-[calc(100%-2rem)] max-w-md rounded-2xl border border-border bg-surface p-5 text-ink shadow-xl backdrop:bg-black/40">
        <h2 id="clear-playlist-title" className="text-lg font-semibold">确认清空列表？</h2>
        <p className="mt-3 text-sm text-muted">{(library.activePlaylistId || 'all') === 'all'
          ? '将清空当前学科的全部口播卡（包括尚未加载的卡片）。原始知识颗粒素材会保留。'
          : `将清空「${playlistName}」中的 ${queue.length} 张口播卡。原始知识颗粒素材会保留。`}</p>
        {clearError ? <p role="alert" className="mt-3 text-sm text-danger">{clearError}</p> : null}
        <div className="mt-5 flex justify-end gap-3">
          <button type="button" autoFocus disabled={clearing} onClick={() => setClearConfirmOpen(false)} className="rounded-full border border-border px-4 py-2 text-sm disabled:opacity-40">取消</button>
          <button type="button" disabled={clearing} onClick={() => { void clearCurrentPlaylist(); }} className="rounded-full bg-danger px-4 py-2 text-sm text-white disabled:opacity-40">{clearing ? '正在清空…' : '确认清空'}</button>
        </div>
      </dialog>
    </div>
  );
}
